import { Intent } from "@blueprintjs/core";
import axios from "axios";
import { ButtonSave } from "components/buttons";
import { ShowHide } from "components/layout";
import { AlertConfirm, AlertUnsavedData, NotificationToaster } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { BaseRetentionPeriod } from "pages/account/edit/data-retention/components/BaseRetentionPeriod";
import { EditDataRetentionForm } from "pages/account/edit/data-retention/components/EditDataRetentionForm";
import { PageRestricted } from "pages/errors/page-restricted";
import React, { useEffect, useMemo, useState } from "react";
import { EmployeeService } from "services";
import { DocumentsService } from "services/DocumentsService";

export function DataRetention() {
    const POLICIES_AREA_NAME = "Policies";
    const DEFAULT_RETENTION_PERIOD_VALUE = 30;
    const DEFAULT_RETENTION_PERIOD_TYPE = "Months";

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [storageActions, setStorageActions] = useState([]);
    const [storageDataRetention, setStorageDataRetention] = useState([]);
    const [showEditDataRetentionForm, setShowEditDataRetentionForm] = useState(false);
    const [documentRetentionRow, setDocumentRetentionRow] = useState({ id: null, period: { value: DEFAULT_RETENTION_PERIOD_VALUE, type: DEFAULT_RETENTION_PERIOD_TYPE }, area: null, isUsingBaseline: false, dataRetentionName: null, baselinePeriod: null, originalPeriod: null });
    const [revertToBaseline, setRevertToBaseline] = useState(false);

    const canAccessPolicies = UserHasAccess('Documents:Manage');

    const autoFrequencyTypeOptionList = [
        { id: "Days", name: "Days" },
        { id: "Months", name: "Months" },
        { id: "Years", name: "Years" },
    ];

    const loadData = async () => {
        const requests = [DocumentsService.getDocumentDataRetention()];

        if (canAccessPolicies) {
            requests.push(EmployeeService.getPolicies(1000, 1, 'Name', 'A')); // Assume we won't have more than 1000 policies
        }

        await axios.all(requests).then(axios.spread((dataRetentionResponse, policiesResponse) => {
            let dataRetentionData = dataRetentionResponse.map((areaItem) => ({
                ...areaItem,
                originalDefaultDataRetentionUnit: areaItem.defaultDataRetentionUnit,
                originalDefaultDataRetentionPeriod: areaItem.defaultDataRetentionPeriod,
                documentTypeDataRetentionPolicies: areaItem.documentTypeDataRetentionPolicies.map((policy) => ({
                    ...policy,
                    originalDataRetentionUnit: policy.dataRetentionUnit,
                    originalDataRetentionPeriod: policy.dataRetentionPeriod,
                })),
            }));
            
            if (canAccessPolicies && policiesResponse) {
                dataRetentionData = [
                    ...dataRetentionData,
                    {
                        area: "Policies",
                        areaName: "Policies",
                        originalDefaultDataRetentionUnit: DEFAULT_RETENTION_PERIOD_VALUE,
                        originalDefaultDataRetentionPeriod: DEFAULT_RETENTION_PERIOD_TYPE,
                        disableDefaultSelection: true,
                        defaultDataRetentionPeriod: DEFAULT_RETENTION_PERIOD_VALUE,
                        defaultDataRetentionUnit: DEFAULT_RETENTION_PERIOD_TYPE,
                        documentTypeDataRetentionPolicies: policiesResponse.data.map(policy => ({
                            documentTypeId: policy.policyId,
                            dataRetentionName: policy.name,
                            dataRetentionUnit: policy.dataRetention?.dataRetentionUnit,
                            dataRetentionPeriod: policy.dataRetention?.dataRetentionPeriod,
                            originalDataRetentionUnit: policy.dataRetention?.dataRetentionUnit,
                            originalDataRetentionPeriod: policy.dataRetention?.dataRetentionPeriod
                        })),
                    }
                ];
            }

            setStorageDataRetention(dataRetentionData);
        }));
    };

    const updateDocumentLevelRetention = ({ id, period, area }) => {
        setStorageDataRetention((prevData) =>
            prevData.map((areaItem) =>
                areaItem.area === area
                    ? {
                        ...areaItem,
                        documentTypeDataRetentionPolicies: areaItem.documentTypeDataRetentionPolicies.map((policy) =>
                            policy.documentTypeId === id
                                ? {
                                    ...policy,
                                    dataRetentionUnit: period.type,
                                    dataRetentionPeriod: period.value,
                                }
                                : policy
                        ),
                    }
                    : areaItem
            )
        );
    }

    const saveEditDataRetentionForm = async ({ id, period, area }) => {

        if (!revertToBaseline) {
            if (period.value === null || period.value === undefined || period.value === "") {
                NotificationToaster.show(Intent.DANGER, "Please enter a retention period value.");
                return;
            }

            if (period.value < 1) {
                NotificationToaster.show(Intent.DANGER, "Please enter a retenion period value greater than 0.");
                return;
            }

            if (!period.type) {
                NotificationToaster.show(Intent.DANGER, "Please select a retention period frequency.");
                return;
            }
        } else {
            period = { period: null, type: null };
        }

        try {
            setIsLoading(true);
            updateDocumentLevelRetention({ id, period, area });
            setShowEditDataRetentionForm(false);
            setRevertToBaseline(false);
        }
        finally {
            setIsLoading(false);
        }

    }

    const openEditDataRetentionForm = async ({ id, period, area, isUsingBaseline, dataRetentionName, baselinePeriod }) => {
        setShowEditDataRetentionForm(true);
        setRevertToBaseline(false);
        setDocumentRetentionRow({ id, period, area, isUsingBaseline, dataRetentionName, baselinePeriod, originalPeriod: { ...period } });
    };

    const updateAreaLevelRetention = (area, newPeriod) => {
        setStorageDataRetention((prevData) =>
            prevData.map((areaItem) =>
                areaItem.area === area
                    ? {
                        ...areaItem,
                        defaultDataRetentionUnit: newPeriod.type,
                        defaultDataRetentionPeriod: newPeriod.value,
                    }
                    : areaItem
            )
        );
    };

    const saveData = useMemo(() => {
        const areaLevelTransformedData = storageDataRetention
            .filter((areaItem) => areaItem.areaName !== POLICIES_AREA_NAME)
            .filter((areaItem) =>
                areaItem.defaultDataRetentionUnit !== areaItem.originalDefaultDataRetentionUnit ||
                areaItem.defaultDataRetentionPeriod !== areaItem.originalDefaultDataRetentionPeriod
            )
            .map((areaItem) => ({
                area: areaItem.area,
                typeId: null,
                featureId: null,
                dataRetentionUnit: areaItem.defaultDataRetentionUnit,
                dataRetentionPeriod: areaItem.defaultDataRetentionPeriod,
            }));

        const documentLevelTransformedData = storageDataRetention
            .flatMap((areaItem) =>
                areaItem.documentTypeDataRetentionPolicies
                    .filter((policy) =>
                        policy.dataRetentionUnit !== policy.originalDataRetentionUnit ||
                        policy.dataRetentionPeriod !== policy.originalDataRetentionPeriod
                    )
                    .map((policy) => ({
                        area: null,
                        typeId: policy.documentTypeId,
                        featureId: policy.featureId,
                        dataRetentionUnit: policy.dataRetentionUnit,
                        dataRetentionPeriod: policy.dataRetentionPeriod,
                        isPolicy: areaItem.areaName === POLICIES_AREA_NAME
                    }))
        );

        return [...areaLevelTransformedData, ...documentLevelTransformedData];
    }, [storageDataRetention]);

    const saveAll = async () => {
        try {
            setIsSaving(true);
            const policyDataRetentions = saveData.filter(d => d.isPolicy);
            const nonPolicyDataRetentions = saveData.filter(d => !d.isPolicy);
            if (policyDataRetentions.length > 0) {
                await EmployeeService.updatePolicyDataRetentions(policyDataRetentions.map(p => ({
                    policyId: p.typeId,
                    dataRetention: p.dataRetentionPeriod != null && p.dataRetentionUnit != null ? {
                        dataRetentionPeriod: p.dataRetentionPeriod,
                        dataRetentionUnit: p.dataRetentionUnit
                    } : null
                })));
            }

            await DocumentsService.updateDocumentDataRetention(nonPolicyDataRetentions);
            await loadData();
            NotificationToaster.show(Intent.SUCCESS, "Data retention settings have been saved.");
        } catch {
            NotificationToaster.show(Intent.DANGER, "An error occurred while saving data retention settings. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                await loadData();

                const options = await DocumentsService.getDocumentOptions();
                const featureActions = options.documentAreas
                    .flatMap(a => a.featureEnabledDocumentTypes.map(f => f.actionKey));
                setStorageActions([...featureActions, 'Documents:Manage']);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    return (
        <ShowHide evaluator={isLoading} show={(
            <div className="row">
                <div style={{ width: "100%" }}>
                    <h4 className="bp6-heading">Data Retention</h4>
                    {[...Array(4)].map((_, index) => (
                        <div key={`skeleton-${index}`} style={{ marginBottom: "10px" }}>
                            <div className="bp6-skeleton" style={{ height: "20px", width: "60%", marginBottom: "15px" }}></div>
                            <div className="bp6-skeleton" style={{ height: "30px", width: "30%", marginBottom: "15px" }}></div>
                            <div className="bp6-skeleton" style={{ height: "30px", width: "80%" }}></div>
                        </div>
                    ))}
                </div>
            </div>
        )} hide={(
            <UserAccess
                perform={storageActions}
                yes={() => (
                    <>
                        <div className="row">

                            <h4 className="bp6-heading">Data Retention</h4>
                            <p>From here you can manage your document data retention for your account.</p>

                            {storageDataRetention.map(d => (
                                <BaseRetentionPeriod
                                    key={d.area}
                                    title={d.areaName}
                                    area={d.area}
                                    defaultPeriod={{
                                        type: d.defaultDataRetentionUnit,
                                        value: d.defaultDataRetentionPeriod
                                    }}
                                    data={d.documentTypeDataRetentionPolicies}
                                    isLoading={isLoading}
                                    isSaving={isSaving}
                                    onActionClick={openEditDataRetentionForm}
                                    onAreaLevelChange={updateAreaLevelRetention}
                                    canBaselineRetentionPeriodNotBeEdited={d.disableDefaultSelection}
                                />
                            ))}

                        </div>
                        <div className="row">
                            <ButtonSave
                                text="SAVE"
                                onClick={saveAll}
                                loading={isLoading}
                                intent={Intent.PRIMARY}
                                disabled={isSaving}
                            />
                        </div>
                        <AlertConfirm
                            title={documentRetentionRow.isUsingBaseline ? "New Data Retention Period" : "Edit Data Retention Period"}
                            isOpen={showEditDataRetentionForm}
                            onConfirm={saveEditDataRetentionForm.bind(this, {
                                id: documentRetentionRow.id,
                                period: documentRetentionRow.period,
                                area: documentRetentionRow.area
                            })}
                            onCancel={() => setShowEditDataRetentionForm(false)}
                            confirmButtonIntent="primary"
                            confirmButtonText={"Update & Close"}
                            saving={isLoading}>
                            <EditDataRetentionForm
                                isLoading={isLoading}
                                revertToBaseline={revertToBaseline}
                                documentRetentionRow={documentRetentionRow}
                                setDocumentRetentionRow={setDocumentRetentionRow}
                                autoFrequencyTypeOptionList={autoFrequencyTypeOptionList}
                                setRevertToBaseline={setRevertToBaseline}
                            />
                        </AlertConfirm>
                        <AlertUnsavedData
                            isDirty={saveData.length > 0}>
                        </AlertUnsavedData>
                    </>
                )}
                no={() => (
                    <div className="row">
                        <PageRestricted />
                    </div>
                )}
            />)} />
    )
}